<template>
  <div class="editor-container-wrapper" :class="{ 'has-split': showExpressionEditor }" :style="wrapperStyle">
    <div class="editor-main-area" :style="mainAreaStyle">
      <el-switch
        class="switch"
        v-model="useMonacoEditor"
        active-text="新编辑器（0.1-beta）"
        inactive-text="原编辑器"
        @change="handleChangeSwitch"
        style="display: none;"
      ></el-switch>
      <div v-show="!useMonacoEditor" :id="editorId" class="jsoneditor-container"></div>
      <iframe v-if="useMonacoEditor" ref="iframe" class="iframe-container" :src="iframeSrc" />
    </div>
    <!-- 可拖动的分隔线 -->
    <div 
      v-if="showExpressionEditor" 
      class="splitter" 
      @mousedown="startResize"
    ></div>
    <!-- 右侧表达式编辑区域 -->
    <div v-if="showExpressionEditor" class="expression-editor-area" :style="editorAreaStyle">
      <div class="expression-editor-header">
        <span>表达式编辑器【正在编辑第 <span class="row-number">{{ editingRowNumber }}</span> 行】</span>
        <div class="header-buttons">
          <el-button 
            type="text" 
            size="small"
            @click="closeExpressionEditor"
            class="close-btn"
          >放弃更改</el-button>
          <el-button 
            type="text" 
            size="small"
            @click="saveExpression"
            class="save-btn"
          >保存表达式</el-button>
        </div>
      </div>
      <div class="expression-editor-content">
        <textarea ref="expressionEditorTextarea" :id="expressionEditorTextareaId"></textarea>
      </div>
    </div>
  </div>
</template>

<script>
import JSONEditor from 'jsoneditor';
import bfjs from 'js-beautify';
import codemirror from 'codemirror';

const throttle = function (fn, ms) {
  let timing = null;
  return function (...arg) {
    if (!timing) {
      timing = setTimeout(() => {
        fn.apply(this, arg);
        timing = null;
      }, ms);
    }
  };
};

const distance = function (a, b) {
  return Math.sqrt((a[0] - b[0]) ** 2 + (a[1] - b[1]) ** 2);
};

const parttenMached = function (raw) {
  if ((raw.includes('"{{') && raw.includes('}}"')) || (raw.includes('"{%') && raw.includes('%}"'))) return true;
  return false;
};

const getExpression = function (raw) {
  let si = raw.indexOf('"{{');
  let ei = raw.indexOf('}}"');
  if(si === -1 || ei === -1) {
    si = raw.indexOf('"{%');
    ei = raw.indexOf('%}"');
    if(si === -1 || ei === -1) {
      return null;
    }
  }
  return raw.slice(si + 3, ei);
};

export default {
  name: 'EditorContainer',
  props: {
    json: {
      type: String,
      default: '{}',
    },
    mode: {
      type: String,
      default: 'code',
    },
    jsonData: {
      type: String,
      default: '{}',
    },
    rootDomSelector: {
      type: String,
      default: '#jsonEditComponent',
    },
    iframeSrc: {
      type: String,
      default: '/json-edit',
    },
  },
  data() {
    return {
      editor: null, // 编辑器对象
      useMonacoEditor: false, // 带json schema的编辑器
      showExpressionEditor: false, // 是否显示表达式编辑器
      editingRowNumber: null, // 正在编辑的行号
      expressionEditor: null, // 表达式编辑器实例
      splitPosition: 60, // 分割位置百分比，默认左侧60%
      isResizing: false, // 是否正在调整大小
      currentEditingData: null, // 当前编辑的数据（用于保存）
      editorId: `jsoneditor-${Date.now()}`, // 唯一的编辑器ID
      expressionEditorTextareaId: `expressionEditorTextarea-${Date.now()}`, // 唯一的textarea ID
    };
  },
  computed: {
    wrapperStyle() {
      return {
        display: 'flex',
        position: 'relative',
        height: '100%'
      };
    },
    mainAreaStyle() {
      if (this.showExpressionEditor) {
        return {
          width: `calc(${this.splitPosition}% - 2px)`,
          marginRight: '4px',
          transition: 'width 0.1s ease'
        };
      }
      return {
        flex: 1
      };
    },
    editorAreaStyle() {
      if (this.showExpressionEditor) {
        return {
          width: `calc(${100 - this.splitPosition}% - 2px)`,
          marginLeft: '4px',
          transition: 'width 0.1s ease'
        };
      }
      return {};
    },
  },
  watch: {
    jsonData: {
      handler(newVal) {
        if (this.editor && !this.useMonacoEditor) {
          try {
            const data = newVal ? JSON.parse(newVal) : {};
            this.editor.set(data);
          } catch (err) {
            console.error(err);
          }
        }
      },
      immediate: false,
    },
    json: {
      handler(newVal) {
        if (this.editor && !this.useMonacoEditor) {
          try {
            const data = newVal ? JSON.parse(newVal) : {};
            this.editor.set(data);
          } catch (err) {
            console.error(err);
          }
        }
      },
      immediate: false,
    },
  },
  mounted() {
    this.initEditor();
    this.initEditorClickHandler();
    window.addEventListener('message', this.handleIframeChange);
  },
  beforeDestroy() {
    window.removeEventListener('message', this.handleIframeChange);
    // 清理表达式编辑器
    if (this.expressionEditor) {
      this.expressionEditor.toTextArea();
      this.expressionEditor = null;
    }
    // 清理JSONEditor
    if (this.editor) {
      this.editor.destroy();
      this.editor = null;
    }
  },
  methods: {
    // 初始化编辑器
    initEditor() {
      this.$nextTick(() => {
        setTimeout(() => {
          const dom = document.getElementById(this.editorId);
          if (!dom) return;
          dom.innerHTML = ''; // 防止重复触发
          this.editor = new JSONEditor(dom, {
            language: 'zh-CN',
            mode: this.mode,
            modes: ['code', 'tree', 'view', 'text'],
            search: true,
          });

          // set json
          let data = this.jsonData || this.json || '{}';
          try {
            data = data ? JSON.parse(data) : {};
          } catch (err) {
            console.log(err);
          }
          this.editor.set(data);
          if (this.mode === 'tree') {
            // 展开所有节点(tree模式)
            this.editor.expandAll();
          }
          
          // 通知父组件编辑器已准备好
          this.$emit('editor-ready', this.editor);
        }, 100);
      });
    },
    // 初始化编辑器点击事件处理
    initEditorClickHandler() {
      this.$nextTick(() => {
        setTimeout(() => {
          const editorDom = document.querySelector('.ace_content');
          const rootDom = document.querySelector(this.rootDomSelector);
          if (!editorDom || !rootDom) return;
          
          editorDom.addEventListener('click', () => {
            // 如果右侧表达式编辑器已经打开，不显示左侧的编辑按钮
            if (this.showExpressionEditor) return;
            
            const raw = this.getLine(editorDom, 'peek');
            if (!parttenMached(raw)) return;
            
            const clickX = event.clientX;
            const clickY = event.clientY;
            const MOVE_SENSITIVITY = 20;
            let counter = MOVE_SENSITIVITY;
            // show button
            const editButton = document.createElement('button');
            editButton.textContent = '编辑表达式';
            editButton.style.position = 'fixed';
            editButton.style.left = `${clickX}px`;
            editButton.style.top = `${clickY - 35}px`;
            editButton.style.padding = '4px 8px';
            editButton.style.fontSize = '12px';
            editButton.style.backgroundColor = '#295FDF';
            editButton.style.color = 'white';
            editButton.style.border = 'none';
            editButton.style.borderRadius = '4px';
            editButton.style.cursor = 'pointer';
            editButton.style.opacity = 0.5;
            editButton.style['z-index'] = 200000;
            editButton.style.boxShadow = '0 2px 4px rgba(0,0,0,0.2)';
            editButton.style.transition = 'opacity 0.2s ease';
            rootDom.appendChild(editButton);
            const distanceHandler = throttle((e) => {
              const a = [e.clientX, e.clientY];
              const b = [clickX, clickY];
              const calculateDistance = distance(a, b);
              if (calculateDistance > 30) {
                decompose();
              }
            }, 100);
            const observer = new MutationObserver((mutationsList) => {
              for (const mutation of mutationsList) {
                if (mutation.type === 'attributes' && mutation.attributeName === 'style') {
                  counter--;
                  if (counter < 0) {
                    decompose();
                  }
                }
              }
            });
            const decompose = () => {
              if (rootDom.contains(editButton)) {
                rootDom.removeChild(editButton);
              }
              editorDom.removeEventListener('mousemove', distanceHandler);
              editorDom.removeEventListener('click', decompose);
              observer.disconnect();
              counter = MOVE_SENSITIVITY;
            };
            editButton.onclick = (e) => {
              e.stopPropagation();
              this.getLine(editorDom);
              decompose();
            };
            editButton.addEventListener('mouseover', () => {
              editButton.style.opacity = '1';
            });
            editButton.addEventListener('mouseout', () => {
              editButton.style.opacity = '0.5';
            });
            editorDom.addEventListener('mousemove', distanceHandler);
            editorDom.addEventListener('click', decompose);
            observer.observe(editorDom, { attributes: true });
            setTimeout(decompose, 10000);
          });
        }, 500);
      });
    },
    getLine(ref, mod) {
      const srow = this.editor.getTextSelection()?.start?.row;
      const erow = this.editor.getTextSelection()?.end?.row;
      if (srow === erow) {
        // get from editor
        const jsonObj = JSON.parse(this.editor.getText()) ?? {};
        const jsonObjList = JSON.stringify(jsonObj, null, 2)?.split('\n') || [];
        const line = jsonObjList[srow - 1];
        if (mod === 'peek') return line;
        const content = getExpression(line);
        if (!content) return srow;
        
        const bfjsOption = {
          max_preserve_newlines: '1',
          break_chained_methods: true,
          wrap_line_length: '70',
        };
        const formattedContent = bfjs.js(content.trim(), bfjsOption);

        // 显示表达式编辑器（srow 是从1开始的行号）
        this.editingRowNumber = srow;
        this.showExpressionEditor = true;
        
        // 通知父组件表达式编辑器已打开
        this.$emit('expression-editor-open');
        
        // 保存当前编辑的数据
        this.currentEditingData = {
          srow,
          line,
          content,
          jsonObjList,
          expressionStartIndex: line.indexOf('"{{') !== -1 ? line.indexOf('"{{') + 3 : (line.indexOf('"{%') !== -1 ? line.indexOf('"{%') + 3 : 0)
        };
        
        // 设置左侧编辑器为只读
        this.setEditorReadOnly(true);
        
        // 等待DOM更新后初始化编辑器
        this.$nextTick(() => {
          this.initExpressionEditor(formattedContent, srow, line, content, jsonObjList);
        });
        
        return srow;
      }
    },
    // 初始化表达式编辑器
    initExpressionEditor(formattedContent, srow, line, content, jsonObjList) {
      // 如果已有编辑器实例，先销毁
      if (this.expressionEditor) {
        this.expressionEditor.toTextArea();
        this.expressionEditor = null;
      }
      
      const textarea = document.getElementById(this.expressionEditorTextareaId);
      if (!textarea) return;
      
      textarea.value = formattedContent;
      
      const bfjsOption = {
        max_preserve_newlines: '1',
        break_chained_methods: true,
        wrap_line_length: '70',
      };
      
      this.expressionEditor = codemirror.fromTextArea(textarea, {
        lineNumbers: true,
        mode: 'javascript',
        theme: 'default',
      });
      
      this.expressionEditor.focus();
      
      const { editor } = this;
      const self = this; // 保存 this 引用，以便在 keyMap 中使用
      codemirror.keyMap.custom = {
        'Cmd-L'(cm) {
          const value = cm.getValue();
          const linted = bfjs.js(value.trim(), bfjsOption);
          cm.setValue(linted);
        },
        'Cmd-S'(cm) {
          const value = cm.getValue();
          const compressed = value
            ?.split(/(\r\n|\n|\r)/gm)
            .map((i) => i.trim())
            .join('');
          jsonObjList[srow - 1] = line.replace(content, compressed);
          editor?.set(JSON.parse(jsonObjList.join('')));
          // 保存后关闭表达式编辑器
          self.closeExpressionEditor();
          // 定位到被修改的行并将光标置于表达式开头
          self.$nextTick(() => {
            // 从编辑器获取更新后的行内容，计算表达式位置
            try {
              const aceEditor = editor.aceEditor || editor.ace;
              if (aceEditor) {
                const session = aceEditor.getSession();
                const updatedLine = session.getLine(srow - 1);
                if (updatedLine) {
                  const expressionStartIndex = updatedLine.indexOf('"{{') !== -1 
                    ? updatedLine.indexOf('"{{') + 3 
                    : (updatedLine.indexOf('"{%') !== -1 ? updatedLine.indexOf('"{%') + 3 : 0);
                  self.navigateToExpressionLine(srow - 1, expressionStartIndex);
                }
              }
            } catch (error) {
              console.error('定位失败:', error);
            }
          });
        },
      };
      this.expressionEditor.addKeyMap('custom');
    },
    // 保存表达式
    saveExpression() {
      if (!this.expressionEditor || !this.currentEditingData) {
        return;
      }
      
      const { srow, line, content, jsonObjList } = this.currentEditingData;
      const value = this.expressionEditor.getValue();
      const compressed = value
        ?.split(/(\r\n|\n|\r)/gm)
        .map((i) => i.trim())
        .join('');
      
      jsonObjList[srow - 1] = line.replace(content, compressed);
      this.editor?.set(JSON.parse(jsonObjList.join('')));
      
      // 通知父组件数据已更新
      const updatedJson = jsonObjList.join('');
      this.$emit('update:jsonData', updatedJson);
      this.$emit('json-change', updatedJson);
      
      // 保存成功后关闭编辑器
      this.closeExpressionEditor();
      
      // 定位到被修改的行并将光标置于表达式开头
      this.$nextTick(() => {
        // 从编辑器获取更新后的行内容，计算表达式位置
        try {
          const aceEditor = this.editor.aceEditor || this.editor.ace;
          if (aceEditor) {
            const session = aceEditor.getSession();
            const updatedLine = session.getLine(srow - 1);
            if (updatedLine) {
              const expressionStartIndex = updatedLine.indexOf('"{{') !== -1 
                ? updatedLine.indexOf('"{{') + 3 
                : (updatedLine.indexOf('"{%') !== -1 ? updatedLine.indexOf('"{%') + 3 : 0);
              this.navigateToExpressionLine(srow - 1, expressionStartIndex);
            }
          }
        } catch (error) {
          console.error('定位失败:', error);
        }
      });
    },
    // 关闭表达式编辑器
    closeExpressionEditor() {
      if (this.expressionEditor) {
        this.expressionEditor.toTextArea();
        this.expressionEditor = null;
      }
      this.showExpressionEditor = false;
      this.editingRowNumber = null;
      this.currentEditingData = null;
      
      // 恢复左侧编辑器为可编辑
      this.setEditorReadOnly(false);
      
      // 通知父组件表达式编辑器已关闭
      this.$emit('expression-editor-close');
    },
    // 设置编辑器只读状态
    setEditorReadOnly(readOnly) {
      if (!this.editor) return;
      
      // 如果是 code 模式，设置 Ace Editor 为只读
      if (this.mode === 'code') {
        const aceEditor = this.editor.aceEditor || this.editor.ace;
        if (aceEditor) {
          aceEditor.setReadOnly(readOnly);
        }
      } else {
        // 其他模式使用 JSONEditor 的 API
        if (this.editor.setOptions) {
          this.editor.setOptions({ readOnly: readOnly });
        }
      }
    },
    // 定位到表达式所在行并将光标置于表达式开头
    navigateToExpressionLine(row, column) {
      if (!this.editor || this.mode !== 'code') return;
      
      try {
        const aceEditor = this.editor.aceEditor || this.editor.ace;
        if (!aceEditor) return;
        
        // 定位到指定行和列
        aceEditor.moveCursorTo(row, column);
        // 滚动到该行，使其可见
        aceEditor.scrollToLine(row, true, true, () => {});
        // 聚焦编辑器
        aceEditor.focus();
      } catch (error) {
        console.error('定位到表达式行失败:', error);
      }
    },
    // 开始调整大小
    startResize(e) {
      this.isResizing = true;
      const wrapper = e.currentTarget.parentElement;
      const startX = e.clientX;
      const startWidth = wrapper.offsetWidth;
      const startLeftPercent = this.splitPosition;
      
      // 使用节流优化性能
      const throttledAdjustHeight = throttle(() => {
        this.$nextTick(() => {
          this.adjustEditorHeight();
        });
      }, 100);
      
      const doResize = (moveEvent) => {
        const diffX = moveEvent.clientX - startX;
        const diffPercent = (diffX / startWidth) * 100;
        let newPercent = startLeftPercent + diffPercent;
        
        // 限制在20%到80%之间
        newPercent = Math.max(20, Math.min(80, newPercent));
        this.splitPosition = newPercent;
        
        // 节流调整编辑器高度
        throttledAdjustHeight();
      };
      
      const stopResize = () => {
        this.isResizing = false;
        document.removeEventListener('mousemove', doResize);
        document.removeEventListener('mouseup', stopResize);
        // 停止拖动后调整编辑器高度
        this.$nextTick(() => {
          this.adjustEditorHeight();
        });
      };
      
      document.addEventListener('mousemove', doResize);
      document.addEventListener('mouseup', stopResize);
      e.preventDefault();
    },
    // 调整编辑器高度
    adjustEditorHeight() {
      // 调整 CodeMirror 编辑器高度
      if (this.expressionEditor) {
        this.expressionEditor.refresh();
      }
      
      // 调整 JSONEditor 高度（如果使用原编辑器）
      if (this.editor && this.editor.aceEditor) {
        const aceEditor = this.editor.aceEditor;
        if (aceEditor && aceEditor.resize) {
          aceEditor.resize();
        }
      }
    },
    handleIframeChange(event) {
      if (event.origin !== window.location.origin) {
        // 确保消息来自可信任的源
        return;
      }
      if (event?.data?.origin !== 'EDITOR') {
        return;
      }
      if (event.data?.type === 'READY') {
        this.$refs.iframe.contentWindow.postMessage(
          {
            value: this.jsonData || this.json,
            origin: 'EDITOR',
          },
          '*',
        );
      } else if (event.data?.type === 'CHANGE') {
        this.$emit('update:jsonData', event.data?.value ?? '{}');
        this.$emit('json-change', event.data?.value ?? '{}');
      }
    },
    handleChangeSwitch() {
      try {
        const data = (this.jsonData || this.json) ? JSON.parse(this.jsonData || this.json) : {};
        if (!this.useMonacoEditor) {
          this.editor.set(data);
        }
      } catch (err) {
        console.error(err);
      }
    },
    // 获取编辑器实例
    getEditor() {
      return this.editor;
    },
    // 获取表达式编辑器是否打开
    isExpressionEditorOpen() {
      return this.showExpressionEditor;
    },
  },
};
</script>

<style lang="scss" scoped>
.editor-container-wrapper {
  display: flex;
  position: relative;
  height: 100%;
}

.editor-main-area {
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.jsoneditor-container {
  height: calc(98vh - 54px - 20px - 70px - 20px);
  // dialog 标题栏 54px body padding 20px  底部操作栏 70px dialog 底部 padding 20px
}

// 分隔线
.splitter {
  width: 4px;
  background-color: #dcdfe6;
  cursor: col-resize;
  position: relative;
  flex-shrink: 0;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: #409eff;
  }
  
  &::before {
    content: '';
    position: absolute;
    left: -2px;
    right: -2px;
    top: 0;
    bottom: 0;
    cursor: col-resize;
  }
}

// 表达式编辑区域
.expression-editor-area {
  display: flex;
  flex-direction: column;
  border: 1px solid #DDD;
  background-color: #fff;
  overflow: hidden;
  height: calc(98vh - 54px - 20px - 70px - 20px);
  max-height: calc(98vh - 54px - 20px - 70px - 20px);
}

.expression-editor-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  border-bottom: 1px solid #dcdfe6;
  background-color: #f5f7fa;
  font-size: 14px;
  color: #606266;
  flex-shrink: 0; // 防止 header 被压缩
  height: 37px; // 固定 header 高度
  box-sizing: border-box;
  
  .header-buttons {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  
  .row-number {
    font-weight: bold;
    font-size: 18px;
    color: #409eff;
  }
}

.expression-editor-content {
  flex: 1;
  overflow: hidden;
  position: relative;
  min-height: 0; // 确保 flex 子元素可以收缩
  
  textarea {
    width: 100%;
    height: 100%;
    border: none;
    resize: none;
  }
  
  ::v-deep .CodeMirror {
    height: 100% !important;
    width: 100% !important;
    max-width: 100% !important;
    max-height: 100% !important;
    font-size: 14px;
  }
  
  ::v-deep .CodeMirror-scroll {
    width: 100%;
    max-width: 100%;
    height: 100%;
    max-height: 100%;
  }

  ::v-deep .CodeMirror-scroll::after {
    content: "【command + s: 保存表达式，command + l：格式化表达式】";
    opacity: 0.2;
    width: 70%;
    position: absolute;
    left: 28px;
    margin-top: 15px;
  }
}
</style>

